import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { confirmQuestion } from 'components/v2/confirm-dialog/confirmQuestion';
import ConfirmWithCommentDialog, { ConfirmWithCommentPayload } from 'components/v2/dialogs/ConfirmWithCommentDialog';
import { DataTableStyled } from 'components/v2/compiler/grids/StyledGrid';
import { toaster } from 'components/v2/toast';
import { useIsSystemAdmin } from 'hooks/useRoleCheck';
import { withGreyBackground } from 'pages/v2/WithGreyBackground';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useCallback, useState } from 'react';
import { Link, Redirect, useParams } from 'react-router-dom';
import EpdRoutes, { EpdLinks } from 'routes/EpdRoutes';
import {
  CompanyEpdAccessStatusModel,
  CompanyFeatureFlagKey,
  EpdAccessState,
  EpdCopyVisibleFormat,
  EpdFormat,
  getAdminCompanyEpdAccessStatuses,
  getAdminCompanyFeatureFlags,
  setAdminCompanyFeatureFlag,
  setAdminEpdAccessStatus,
  switchAdminCompanyEpdCopies,
  switchAdminEpdCopy,
} from 'services/api/adminEpdAccessApi';
import { QUERY_KEYS } from 'services/api/constants';
import styled from 'styled-components';
import { Container } from 'styles/Styles.styled';
import { FormPanel, H1NewUI } from 'styles/v2/Styles.styled';
import { getApiErrorDetail } from 'util/apiErrorUtils';
import CompanyMigrationRuns from './CompanyMigrationRuns';

const STATUS_LABELS: Record<EpdAccessState, string> = {
  Editable: 'Editable',
  ReadOnly: 'Read-only',
  Hidden: 'Hidden',
};

const FORMAT_LABELS: Record<EpdFormat, string> = {
  SimpleWizard: 'SW',
  Compiler: 'Compiler',
  DynamicForms: 'DF',
};

const COPY_LABELS: Record<EpdCopyVisibleFormat, string> = {
  SimpleWizard: 'SW',
  DynamicForms: 'DF',
};

const STATUS_OPTIONS = (Object.keys(STATUS_LABELS) as EpdAccessState[]).map((value) => ({
  value,
  label: STATUS_LABELS[value],
}));

type PendingStatusChange = {
  row: CompanyEpdAccessStatusModel;
  status: EpdAccessState;
};

const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleString() : '');

const describeRow = (row: CompanyEpdAccessStatusModel) =>
  [row.registrationNumber, row.name, `(${FORMAT_LABELS[row.format] ?? row.format})`].filter((part) => !!part).join(' ');

const showError = (error: unknown, fallback: string) =>
  toaster({ severity: 'error', summary: 'Error', details: getApiErrorDetail(error) ?? fallback });

/**
 * Company migration screen (System Administrator only): every EPD copy of the company with its access status, Hidden ones
 * included (status data only). Sets a status per copy, switches a migrated EPD between its SW and DF copy (one or
 * all), and locks or unlocks the company. The server refuses anyone who is not a System Administrator.
 */
const CompanyMigrationContent: React.FunctionComponent<{ companyId: string }> = ({ companyId }) => {
  const queryClient = useQueryClient();
  const [pendingStatus, setPendingStatus] = useState<PendingStatusChange | null>(null);

  const listQueryKey = [QUERY_KEYS.ADMIN_COMPANY_EPD_ACCESS_STATUSES, companyId];
  const flagsQueryKey = [QUERY_KEYS.ADMIN_COMPANY_FEATURE_FLAGS, companyId];

  const listQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: () => getAdminCompanyEpdAccessStatuses(companyId),
    enabled: !!companyId,
    refetchOnWindowFocus: false,
  });

  const flagsQuery = useQuery({
    queryKey: flagsQueryKey,
    queryFn: () => getAdminCompanyFeatureFlags(companyId),
    enabled: !!companyId,
    refetchOnWindowFocus: false,
  });

  const refreshList = () => queryClient.invalidateQueries({ queryKey: listQueryKey });

  // A finished rehearsal or rollback adds or removes copies and changes the company lock.
  const handleRunFinished = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_COMPANY_EPD_ACCESS_STATUSES, companyId] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_COMPANY_FEATURE_FLAGS, companyId] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY_FEATURE_FLAGS] });
  }, [queryClient, companyId]);

  const statusMutation = useMutation({
    mutationFn: ({ row, status, reason }: PendingStatusChange & { reason?: string }) =>
      setAdminEpdAccessStatus({
        epdHeadId: row.format === 'DynamicForms' ? undefined : row.epdHeadId,
        documentHeadId: row.format === 'DynamicForms' ? row.documentHeadId : undefined,
        status,
        reason,
      }),
    onSuccess: () => toaster({ severity: 'success', summary: 'Success', details: 'The status has been saved.' }),
    onError: (error) => showError(error, 'Failed to save the status.'),
    onSettled: () => refreshList(),
  });

  const switchMutation = useMutation({
    mutationFn: ({ headId, visibleFormat }: { headId: string; visibleFormat: EpdCopyVisibleFormat }) =>
      switchAdminEpdCopy(headId, visibleFormat),
    onSuccess: (result, { visibleFormat }) =>
      toaster({
        severity: 'success',
        summary: 'Success',
        details: `The ${COPY_LABELS[visibleFormat]} copy is now visible (${result.switched} EPD switched).`,
      }),
    onError: (error) => showError(error, 'Failed to switch the EPD.'),
    onSettled: () => refreshList(),
  });

  const switchAllMutation = useMutation({
    mutationFn: (visibleFormat: EpdCopyVisibleFormat) => switchAdminCompanyEpdCopies(companyId, visibleFormat),
    onSuccess: (result, visibleFormat) =>
      toaster({
        severity: 'success',
        summary: 'Success',
        details: `${result.switched} EPD(s) switched to ${COPY_LABELS[visibleFormat]}.`,
      }),
    onError: (error) => showError(error, 'Failed to switch the EPDs.'),
    onSettled: () => refreshList(),
  });

  const lockMutation = useMutation({
    mutationFn: (isLocked: boolean) => setAdminCompanyFeatureFlag(companyId, CompanyFeatureFlagKey.EpdReadOnly, isLocked),
    onSuccess: (flags) => {
      queryClient.setQueryData(flagsQueryKey, flags);
      toaster({
        severity: 'success',
        summary: 'Success',
        details: flags.readOnly.isEnabled ? 'The company is locked.' : 'The company is unlocked.',
      });
    },
    onError: (error) => showError(error, 'Failed to change the company lock.'),
    onSettled: () => {
      // The dashboard and the other portal pages read the company switches through this key.
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY_FEATURE_FLAGS] });
      refreshList();
    },
  });

  const isBusy =
    statusMutation.isPending || switchMutation.isPending || switchAllMutation.isPending || lockMutation.isPending;
  const isLocked = flagsQuery.data?.readOnly.isEnabled;

  const handleStatusDialogClose = (payload: ConfirmWithCommentPayload | null) => {
    const change = pendingStatus;
    setPendingStatus(null);
    if (!payload || !change) {
      return;
    }

    statusMutation.mutate({ ...change, reason: payload.commentText.trim() || undefined });
  };

  const confirmSwitchAll = (visibleFormat: EpdCopyVisibleFormat) =>
    confirmQuestion({
      caption: `Switch all migrated EPDs to ${COPY_LABELS[visibleFormat]}?`,
      description: `The ${COPY_LABELS[visibleFormat]} copy of every migrated EPD of this company becomes visible and the other copy is hidden.`,
      acceptLabel: 'Switch',
      rejectLabel: 'Cancel',
      onConfirm: () => switchAllMutation.mutate(visibleFormat),
    });

  const confirmLock = (lock: boolean) =>
    confirmQuestion({
      caption: lock ? 'Lock the company?' : 'Unlock the company?',
      description: lock
        ? 'Every EPD of this company becomes read-only and creating EPDs is blocked, for every user.'
        : 'The EPDs of this company become editable again, according to their own status.',
      acceptLabel: lock ? 'Lock' : 'Unlock',
      rejectLabel: 'Cancel',
      onConfirm: () => lockMutation.mutate(lock),
    });

  const statusBodyTemplate = (row: CompanyEpdAccessStatusModel) => (
    <Dropdown
      value={row.status}
      options={STATUS_OPTIONS}
      optionLabel="label"
      optionValue="value"
      disabled={isBusy}
      onChange={(e) => setPendingStatus({ row, status: e.value as EpdAccessState })}
    />
  );

  const switchBodyTemplate = (row: CompanyEpdAccessStatusModel) =>
    row.hasOtherCopy ? (
      <SwitchButtons>
        {(['SimpleWizard', 'DynamicForms'] as EpdCopyVisibleFormat[]).map((visibleFormat) => (
          <Button
            key={visibleFormat}
            label={`Use ${COPY_LABELS[visibleFormat]}`}
            className="p-button-sm p-button-outlined"
            disabled={isBusy}
            onClick={() => switchMutation.mutate({ headId: row.headId, visibleFormat })}
          />
        ))}
      </SwitchButtons>
    ) : null;

  if (listQuery.isLoading) {
    return (
      <Container>
        <ProgressSpinner />
      </Container>
    );
  }

  return (
    <Container>
      <ConfirmDialog />
      <BackLink to={EpdLinks.editCompany(companyId)}>Back to the organisation</BackLink>
      <PageHeader>
        <H1NewUI>EPD migration</H1NewUI>
        <PageActions>
          <Button
            label="Switch all to DF"
            className="p-button-sm"
            disabled={isBusy}
            onClick={() => confirmSwitchAll('DynamicForms')}
          />
          <Button
            label="Switch all to SW"
            className="p-button-sm"
            disabled={isBusy}
            onClick={() => confirmSwitchAll('SimpleWizard')}
          />
          <Button
            label={isLocked ? 'Unlock company' : 'Lock company'}
            icon={isLocked ? 'pi pi-lock-open' : 'pi pi-lock'}
            className="p-button-sm p-button-warning"
            disabled={isBusy || !flagsQuery.data}
            onClick={() => confirmLock(!isLocked)}
          />
        </PageActions>
      </PageHeader>
      {flagsQuery.isError && (
        <ErrorText>{getApiErrorDetail(flagsQuery.error) ?? 'Failed to load the company lock.'}</ErrorText>
      )}
      {isLocked && <LockNotice>The company is locked: every EPD is read-only and creating EPDs is blocked.</LockNotice>}

      <CompanyMigrationRuns companyId={companyId} onRunFinished={handleRunFinished} />

      <SectionTitle>EPD copies and access</SectionTitle>
      <GridPanel>
        {listQuery.isError ? (
          <ErrorText>{getApiErrorDetail(listQuery.error) ?? 'Failed to load the EPDs of the company.'}</ErrorText>
        ) : (
          <DataTableStyled
            value={listQuery.data ?? []}
            sortField="registrationNumber"
            sortOrder={1}
            emptyMessage="The company has no EPDs."
          >
            <Column field="registrationNumber" header="Registration number" sortable style={{ minWidth: '9rem' }} />
            <Column field="name" header="Name" sortable style={{ minWidth: '16rem' }} />
            <Column
              field="format"
              header="Format"
              sortable
              body={(row: CompanyEpdAccessStatusModel) => FORMAT_LABELS[row.format] ?? row.format}
            />
            <Column field="status" header="Status" body={statusBodyTemplate} style={{ minWidth: '10rem' }} />
            <Column
              field="effectiveStatus"
              header="Effective status"
              sortable
              body={(row: CompanyEpdAccessStatusModel) => STATUS_LABELS[row.effectiveStatus] ?? row.effectiveStatus}
            />
            <Column field="reason" header="Reason" style={{ minWidth: '12rem' }} />
            <Column field="modifiedBy" header="Modified by" sortable />
            <Column
              field="modifiedAt"
              header="Modified at"
              sortable
              body={(row: CompanyEpdAccessStatusModel) => formatDate(row.modifiedAt)}
            />
            <Column header="Switch" body={switchBodyTemplate} style={{ minWidth: '12rem' }} />
          </DataTableStyled>
        )}
      </GridPanel>

      <ConfirmWithCommentDialog
        isOpened={!!pendingStatus}
        onClose={handleStatusDialogClose}
        header={pendingStatus ? `Set the status to ${STATUS_LABELS[pendingStatus.status]}` : 'Set the status'}
        help={<p>{pendingStatus ? describeRow(pendingStatus.row) : ''}</p>}
        inputLabelText="Reason (optional)"
        primaryButtonText="Save"
      />
    </Container>
  );
};

const CompanyMigration: React.FunctionComponent = () => {
  const isSystemAdmin = useIsSystemAdmin();
  const { companyid } = useParams<{ companyid: string }>();

  if (!isSystemAdmin) {
    return <Redirect to={EpdRoutes.forbidden} />;
  }

  return <CompanyMigrationContent companyId={companyid} />;
};

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.5rem 0;
`;

const PageActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const SwitchButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const SectionTitle = styled.h3`
  margin: 1.5rem 0 0.5rem;
`;

const GridPanel = styled(FormPanel)`
  display: block;
  padding: 0;
  overflow: hidden;
`;

const BackLink = styled(Link)`
  display: inline-block;
  padding-top: 1rem;
`;

const LockNotice = styled.div`
  padding: 0.5rem 0;
  font-weight: 600;
`;

const ErrorText = styled.div`
  color: ${(props) => props.theme.colors.statusLabelRedText};
  padding: 0.5rem 0;
`;

export default withGreyBackground(CompanyMigration);
